import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { ComponentSync } from '../../core/ComponentSync.js';
import { exists, isPromise, resolveContent } from '@nu-art/ts-common';
import './Button.scss';
import { _className } from '../../utils/tools.js';
import { LL_H_C } from '../Layouts/index.js';
/**
 * Thunderstorm Button component, this component is the de-facto button in our system.</br>
 * This button handles both sync and async actions, and can be autonomous or controlled.</br>
 *
 * @property variant - the variant of the button, will be added to the button element as a "data-variant" attribute.
 * this component comes with 5 default variants ('primary' | 'secondary' | 'tertiary' | 'text' | 'dangerous'), but will accept any string, just make sure to add your own design for it!
 *
 * @property loader - When an async action is fired, the button renders a loader. this property is an overwrite for the loader renderer.
 *
 * @property actionInProgress - If given, the button becomes controlled and will only show a loader if true is passed from the outside.
 */
export class Button extends ComponentSync {
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.actionInProgress ??= false;
        state.disabled = !!nextProps.disabled;
        if (exists(nextProps.actionInProgress))
            state.actionInProgress = nextProps.actionInProgress;
        return state;
    }
    shouldComponentUpdate() {
        return true;
    }
    //######################### Logic #########################
    getClassName = () => {
        return _className('ts-button', this.props.className, this.state.actionInProgress && 'action-in-progress');
    };
    getProps = () => {
        //Clean out non-html properties from the props
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { loader, variant, innerRef, onDisabledClick, actionInProgress, ...rest } = this.props;
        const props = rest;
        //Align props from other sources
        props.className = this.getClassName();
        props.disabled = this.state.disabled;
        props.ref = innerRef;
        props['data-variant'] = variant ?? 'tertiary';
        return props;
    };
    handleAction = async (e) => {
        // skip action if needed
        if (this.state.actionInProgress)
            return;
        if (this.state.disabled)
            return this.props.onDisabledClick?.(e);
        // call the action, the response can be both promise or sync void
        const result = this.props.onClick?.(e);
        // if result is not a promise return
        if (!isPromise(result))
            return;
        const controlledInProgress = exists(this.props.actionInProgress);
        // in case the result is from type promise and needs to be awaited, await and handle errors
        if (!controlledInProgress) {
            // @ts-ignore - prevents race conditions
            // noinspection JSConstantReassignment
            this.state.actionInProgress = true;
            this.forceUpdate();
        }
        try {
            await result;
        }
        catch (error) {
            this.logError(error);
            throw error;
        }
        finally {
            if (!controlledInProgress) {
                // @ts-ignore - prevents race conditions
                // noinspection JSConstantReassignment
                this.state.actionInProgress = false;
                this.forceUpdate();
            }
        }
    };
    //######################### Render #########################
    render() {
        return _jsxs("button", { ...this.getProps(), type: 'button', onClick: this.handleAction, children: [this.renderContent(), this.renderLoader()] });
    }
    renderContent = () => {
        return _jsx(LL_H_C, { className: 'ts-button__content', children: this.props.children });
    };
    renderLoader = () => {
        if (this.props.loader)
            return resolveContent(this.props.loader);
        return _jsx("div", { className: 'ts-button__loader' });
    };
}
export const TS_Button = Button;
//# sourceMappingURL=Button.js.map